/**
 * CellHack Foundation Score quiz (stand-alone page, layout/quiz.liquid).
 * 8 questions, one per screen, across five areas: Sleep, Training, Recovery,
 * Energy, Nutrition. Each answer is worth 0-3 (recovery 0-4) and is turned
 * into a 0-100 area score; the overall score is the average of the five areas
 * and the three lowest areas are shown as the "biggest gaps".
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-tq]');
  if (!root) return;

  var cfg = {};
  try {
    cfg = JSON.parse(root.querySelector('[data-tq-config]').textContent);
  } catch (error) {
    cfg = {};
  }

  var stage = root.querySelector('[data-tq-stage]');
  var topBar = root.querySelector('[data-tq-top]');
  var backBtn = root.querySelector('[data-tq-back]');
  var countEl = root.querySelector('[data-tq-count]');
  var barEl = root.querySelector('[data-tq-bar]');
  var barFill = root.querySelector('[data-tq-bar-fill]');

  /* ---------------------------------------------------------------- data -- */
  // Options are listed best first. The value is the points for that answer.
  var AREAS = {
    sleep: {
      title: 'Sleep',
      icon: '\uD83C\uDF19',
      line: 'Your sleep is the first thing to fix.',
      tip: 'Keep the same bed and wake time every day, switch screens off 45 minutes before bed, keep the room cool and dark, and stop caffeine by 2pm.'
    },
    training: {
      title: 'Training',
      icon: '\uD83C\uDFCB\uFE0F',
      line: 'You are training less than your body can use.',
      tip: 'Start with two full-body strength sessions a week and build up to three. Build them around the big lifts: squat, press, row and deadlift.'
    },
    recovery: {
      title: 'Recovery',
      icon: '\uD83D\uDD04',
      line: 'Your recovery is holding you back.',
      tip: 'Plan an easier week every four to six weeks, sleep 7 to 9 hours and keep protein up on training days.'
    },
    energy: {
      title: 'Energy',
      icon: '\u26A1',
      line: 'Your energy dips more than it should.',
      tip: 'Get daylight in the morning, walk for ten minutes after lunch, eat at regular times and cut caffeine after 2pm.'
    },
    nutrition: {
      title: 'Nutrition',
      icon: '\uD83C\uDF7D\uFE0F',
      line: 'Your food has room to do more for you.',
      tip: 'Build every meal around protein and vegetables. A varied diet is the best source of vitamins and minerals, and a supplement can sit alongside it.'
    }
  };
  var AREA_ORDER = ['sleep', 'training', 'recovery', 'energy', 'nutrition'];

  var QUESTIONS = {
    q1: {
      n: 1,
      area: 'sleep',
      text: 'How many hours do you usually sleep?',
      eyebrow: 'Sleep',
      options: [['7 hours or more', 3], ['6 hours', 2], ['5 hours', 1], ['Under 5 hours', 0]]
    },
    q2: {
      n: 2,
      area: 'sleep',
      text: 'How rested do you feel when you wake up?',
      eyebrow: 'Sleep',
      options: [['Fully rested', 3], ['Mostly rested', 2], ['Tired', 1], ['Exhausted', 0]]
    },
    q3: {
      n: 3,
      area: 'training',
      text: 'How many strength sessions do you do each week?',
      eyebrow: 'Training',
      options: [['3 or more', 3], ['2', 2], ['1', 1], ['None', 0]]
    },
    q4: {
      n: 4,
      area: 'recovery',
      text: 'How would you rate your recovery in the last month?',
      eyebrow: 'Recovery',
      options: [['Very good', 4], ['Good', 3], ['Average', 2], ['Poor', 1], ['Very poor', 0]]
    },
    q5: {
      n: 5,
      area: 'energy',
      text: 'How would you rate your energy levels during the day?',
      eyebrow: 'Energy',
      options: [['High: I feel energetic most of the day', 3], ['Moderate: I have ups and downs', 2], ['Low: I have little energy most days', 1], ['Very low: I often feel tired and unmotivated', 0]]
    },
    q6: {
      n: 6,
      area: 'energy',
      text: 'How often do you get an energy crash in the afternoon?',
      eyebrow: 'Energy',
      options: [['Rarely or never', 3], ['Once or twice a week', 2], ['Most days', 1], ['Every day', 0]]
    },
    q7: {
      n: 7,
      area: 'nutrition',
      text: 'How often do you eat protein with your meals?',
      eyebrow: 'Nutrition',
      options: [['Every meal', 3], ['Most meals', 2], ['Some meals', 1], ['Rarely', 0]]
    },
    q8: {
      n: 8,
      area: 'nutrition',
      text: 'How many portions of fruit and vegetables do you eat on a typical day?',
      eyebrow: 'Nutrition',
      options: [['5 or more', 3], ['3 to 4', 2], ['1 to 2', 1], ['Hardly any', 0]]
    }
  };

  var SEQ = ['intro', 'q1', 'q2', 'reassure', 'q3', 'q4', 'meet', 'q5', 'q6', 'q7', 'q8', 'calc', 'email', 'result'];
  var TOTAL_Q = 8;

  /* -------------------------------------------------------- traffic source -- */
  // Campaign tags from the link they arrived on. Kept in memory only, and only
  // attached to a contact the visitor has chosen to submit with consent.
  var source = (function () {
    var out = {};
    try {
      var params = new URLSearchParams(location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(function (key) {
        var v = (params.get(key) || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
        if (v) out[key] = v;
      });
      if (!out.utm_source && params.get('fbclid')) out.utm_source = 'facebook';
    } catch (e) {}
    return out;
  })();

  /* --------------------------------------------------------------- state -- */
  var answers = {};
  var points = {};
  var history = [];
  var current = 'intro';
  var started = false;
  var contact = { name: '', email: '' };
  var result = null;

  /* ------------------------------------------------------------- helpers -- */
  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function track(name, data) {
    var payload = data || {};
    try {
      (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'ch_quiz_' + name }, payload));
    } catch (e) {}
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('ch_quiz_' + name, payload);
      }
    } catch (e) {}
    try {
      if (typeof window.fbq === 'function') window.fbq('trackCustom', 'Quiz_' + name, payload);
    } catch (e) {}
    try {
      if (typeof window.gtag === 'function') window.gtag('event', 'quiz_' + name, payload);
    } catch (e) {}
  }

  function nextOf(id) {
    return SEQ[SEQ.indexOf(id) + 1];
  }

  function maxPoints(id) {
    return Math.max.apply(null, QUESTIONS[id].options.map(function (o) { return o[1]; }));
  }

  /* ------------------------------------------------------------- scoring -- */
  function tierOf(score) {
    return score >= 75 ? 'strong' : score >= 50 ? 'building' : 'gaps';
  }

  function compute() {
    var areas = {};
    AREA_ORDER.forEach(function (key) {
      var got = 0;
      var max = 0;
      Object.keys(QUESTIONS).forEach(function (id) {
        if (QUESTIONS[id].area !== key) return;
        got += points[id] || 0;
        max += maxPoints(id);
      });
      areas[key] = max ? Math.round((got / max) * 100) : 0;
    });
    var total = 0;
    AREA_ORDER.forEach(function (key) { total += areas[key]; });
    var score = Math.round(total / AREA_ORDER.length);

    // Three lowest areas, ties broken by the order of AREA_ORDER.
    var gaps = AREA_ORDER.map(function (key, index) { return { key: key, value: areas[key], index: index }; })
      .sort(function (a, b) { return a.value - b.value || a.index - b.index; })
      .slice(0, 3)
      .map(function (g) { return g.key; });

    result = { score: score, tier: tierOf(score), areas: areas, gaps: gaps };
    return result;
  }

  /* ---------------------------------------------------------- navigation -- */
  function go(id, isBack) {
    if (!isBack && current && current !== 'calc') history.push(current);
    current = id;
    render(id, isBack);
  }

  function goBack() {
    var prev = history.pop();
    if (prev) go(prev, true);
  }

  backBtn.addEventListener('click', goBack);

  function setProgress(id) {
    var q = QUESTIONS[id];
    var n = 0;
    if (q) n = q.n;
    else if (id === 'reassure') n = 2;
    else if (id === 'meet') n = 4;
    else if (id === 'email' || id === 'calc') n = TOTAL_Q;
    var pct = Math.round((n / TOTAL_Q) * 100);
    barFill.style.width = pct + '%';
    barEl.setAttribute('aria-valuenow', String(pct));
    var label = '';
    if (q) label = 'Question ' + q.n + ' of ' + TOTAL_Q;
    else if (id === 'email') label = 'Last step';
    countEl.textContent = label;
  }

  /* ------------------------------------------------------------- screens -- */
  function screen(html, extraClass, isBack) {
    return '<section class="tq__screen ' + (extraClass || '') + (isBack ? ' is-back' : '') + '" tabindex="-1">' + html + '</section>';
  }

  function renderIntro(isBack) {
    var heroSrc = cfg.heroImage || cfg.productImage;
    var hero = heroSrc ? '<div class="tq__hero"><img src="' + esc(heroSrc) + '" alt="A man smiling and pointing at a bottle of ' + esc(cfg.productTitle) + '" width="600" height="600"></div>' : '';
    return screen(
      '<span class="tq__eyebrow">Foundation Score &middot; 8 questions &middot; 60 sec</span>' +
        '<h1 class="tq__title">What&rsquo;s your <em>number?</em></h1>' +
        '<p class="tq__sub">Answer 8 quick questions and see how your lifestyle stacks up across sleep, training, recovery, energy and nutrition.</p>' +
        hero +
        '<ul class="tq__checks">' +
        '<li><span class="tq__check-mark">&#10003;</span><span class="tq__check-title">Your score out of 100</span><span class="tq__check-text">One number that sums up your foundation</span></li>' +
        '<li><span class="tq__check-mark">&#10003;</span><span class="tq__check-title">Your five area scores</span><span class="tq__check-text">Sleep, training, recovery, energy and nutrition</span></li>' +
        '<li><span class="tq__check-mark">&#10003;</span><span class="tq__check-title">Your 3 biggest gaps</span><span class="tq__check-text">Each with one practical tip you can use this week</span></li>' +
        '</ul>' +
        '<button type="button" class="tq__btn" data-start>Start the quiz</button>' +
        '<p class="tq__small tq__center">8 questions, 60 seconds, free.</p>',
      '',
      isBack
    );
  }

  function renderQuestion(id, isBack) {
    var q = QUESTIONS[id];
    var html = '';
    html += '<span class="tq__eyebrow">' + esc(q.eyebrow) + '</span>';
    html += '<h2 class="tq__q">' + esc(q.text) + '</h2>';
    html += '<div class="tq__options" role="group" aria-label="' + esc(q.text) + '">';
    q.options.forEach(function (opt) {
      var selected = answers[id] === opt[0];
      html += '<button type="button" class="tq__option tq__option--plain' + (selected ? ' is-selected' : '') + '" data-q="' + id + '" data-value="' + esc(opt[1]) + '" data-label="' + esc(opt[0]) + '"><span>' + esc(opt[0]) + '</span></button>';
    });
    html += '</div>';
    return screen(html, '', isBack);
  }

  function renderReassure(isBack) {
    return screen(
      '<span class="tq__eyebrow">Good to know</span>' +
        '<h2 class="tq__q">Most men notice the same three things.</h2>' +
        '<p class="tq__sub">Sleep, training and food set the base for how you feel day to day. Your answers will show which of the five areas needs attention first. There are no wrong answers.</p>' +
        '<button type="button" class="tq__btn" data-next>Let&rsquo;s keep going</button>',
      '',
      isBack
    );
  }

  function renderMeet(isBack) {
    var name = esc(cfg.productTitle || 'Toro Prime');
    var img = cfg.productImage ? '<img class="tq__bottle" src="' + esc(cfg.productImage) + '" alt="' + name + '" width="220" height="275">' : '';
    return screen(
      '<span class="tq__eyebrow">Meet ' + name + '</span>' +
        '<h2 class="tq__q">Built to sit alongside your habits.</h2>' +
        img +
        '<ul class="tq__claims">' +
        '<li><strong>Zinc, 10 mg</strong>Contributes to the maintenance of normal testosterone levels in the blood.</li>' +
        '<li><strong>Vitamin D3, 25 &micro;g</strong>Contributes to the maintenance of normal muscle function.</li>' +
        '<li><strong>Vitamin B6, 1.4 mg</strong>Contributes to the reduction of tiredness and fatigue.</li>' +
        '</ul>' +
        '<p class="tq__note-fine">Nine actives, every dose printed on the label. A food supplement, not a substitute for a varied diet.</p>' +
        '<button type="button" class="tq__btn" data-next>Continue the quiz</button>',
      'tq__screen--dark',
      isBack
    );
  }

  function renderCalc() {
    return screen(
      '<h2 class="tq__q">Calculating your score</h2>' +
        '<ul class="tq__steps">' +
        '<li class="tq__step" data-step>Scoring sleep and training</li>' +
        '<li class="tq__step" data-step>Checking recovery and energy</li>' +
        '<li class="tq__step" data-step>Finding your biggest gaps</li>' +
        '</ul>',
      'tq__calc'
    );
  }

  function renderEmail(isBack) {
    return screen(
      '<span class="tq__pill">Save 20% on Toro Prime</span>' +
        '<h2 class="tq__q">Unlock 20% off with your email.</h2>' +
        '<p class="tq__sub">Your score is ready. Enter your email to get your result, your three biggest gaps and your quiz offer.</p>' +
        '<div data-email-slot></div>',
      '',
      isBack
    );
  }

  /* -------------------------------------------------------------- result -- */
  function areaColor(value) {
    return value >= 75 ? 'var(--tq-strong)' : value >= 50 ? 'var(--tq-building)' : 'var(--tq-gaps)';
  }

  function areasBlock() {
    var r = result;
    var html = '<div class="tq__areas">';
    AREA_ORDER.forEach(function (key) {
      var v = r.areas[key];
      html += '<div class="tq__area"><span class="tq__area-name">' + esc(AREAS[key].title) + '</span>' +
        '<span class="tq__split-track"><span class="tq__split-fill" data-fill="' + v + '" style="background:' + areaColor(v) + '"></span></span>' +
        '<span class="tq__area-val">' + v + '<small>/100</small></span></div>';
    });
    return html + '</div>';
  }

  function gapsBlock() {
    var r = result;
    var html = '<div class="tq__block"><h3 class="tq__h3">Your 3 biggest gaps</h3>';
    r.gaps.forEach(function (key, i) {
      var a = AREAS[key];
      html += '<div class="tq__factor"><span class="tq__factor-n">' + (i + 1) + '</span><span class="tq__factor-title">' + esc(a.title) + ' &middot; ' + r.areas[key] + '/100</span><span class="tq__factor-tip"><strong>' + esc(a.line) + '</strong> ' + esc(a.tip) + '</span></div>';
    });
    return html + '</div>';
  }

  function priceLine() {
    if (cfg.livePrice) {
      var line = 'From <strong>' + esc(cfg.livePrice) + '</strong>';
      if (cfg.liveWas) line += ' <s>' + esc(cfg.liveWas) + '</s> &middot; ' + esc(cfg.livePct) + '% off for quiz takers';
      return line;
    }
    return 'From ' + esc(cfg.productPrice || '');
  }

  function offerBlock() {
    var r = result;
    var url = cfg.productUrl + (cfg.productUrl.indexOf('?') > -1 ? '&' : '?') + 'utm_source=quiz&utm_medium=onsite&utm_campaign=foundation-score';
    var name = cfg.productTitle || 'Toro Prime';
    var img = cfg.productImage ? '<img class="tq__bottle" src="' + esc(cfg.productImage) + '" alt="' + esc(name) + '" width="220" height="275">' : '';
    var gapNames = r.gaps.map(function (k) { return AREAS[k].title.toLowerCase(); });
    var gapText = gapNames.length > 1 ? gapNames.slice(0, -1).join(', ') + ' and ' + gapNames[gapNames.length - 1] : gapNames[0];
    return (
      '<div class="tq__offer" data-offer>' +
      '<span class="tq__pill">Your quiz offer</span>' +
      '<h3 class="tq__offer-title">20% off ' + esc(name) + '</h3>' +
      img +
      '<p class="tq__offer-price">' + priceLine() + '</p>' +
      '<p class="tq__offer-line">A food supplement for adult men, built to sit alongside your plan for ' + esc(gapText) + '. Nine actives, every dose printed on the label. It does not replace good sleep, training and food.</p>' +
      '<a class="tq__btn" data-cta href="' + esc(url) + '">Claim my 20% off</a>' +
      '<p class="tq__note-fine">Applied automatically on the next page. 30-day money-back guarantee on your first order.</p>' +
      '</div>'
    );
  }

  function claimsBlock() {
    return (
      '<div class="tq__block"><h3 class="tq__h3">What is in it</h3>' +
      '<ul class="tq__ingredients">' +
      '<li><strong>Zinc, 10 mg</strong>Zinc contributes to the maintenance of normal testosterone levels in the blood.</li>' +
      '<li><strong>Vitamin D3, 25 &micro;g</strong>Vitamin D contributes to the maintenance of normal muscle function.</li>' +
      '<li><strong>Vitamin B6, 1.4 mg</strong>Vitamin B6 contributes to the reduction of tiredness and fatigue and to the regulation of hormonal activity.</li>' +
      '</ul>' +
      '<p class="tq__small">Plus 250 mg bull testicle extract and four botanicals. No health claims are made for those.</p></div>'
    );
  }

  function proofBlock() {
    var html = '<div class="tq__proof">';
    if (cfg.ratingCount > 0) {
      html += '<div class="tq__proof-head"><span class="tq__stars" aria-hidden="true">&#9733;&#9733;&#9733;&#9733;&#9733;</span><span class="tq__proof-rating">' + esc(cfg.rating) + ' from ' + esc(cfg.ratingCount) + ' reviews</span></div>';
    }
    (cfg.reviews || []).forEach(function (r) {
      html += '<div class="tq__review"><strong>' + esc(r.title) + '</strong>' + esc(r.body) + '<span>' + esc(r.name) + (r.verified ? ' &middot; Verified buyer' : '') + '</span></div>';
    });
    html += '<p class="tq__small">Individual experiences. Results vary.</p></div>';
    html += '<div class="tq__guarantee">&#10003; 30-day money-back guarantee &middot; <a href="' + esc(cfg.returnsUrl) + '">how it works</a></div>';
    return html;
  }

  function renderResult() {
    var r = result || compute();
    var labels = { strong: 'Strong foundation', building: 'Room to improve', gaps: 'Clear gaps' };
    var text = {
      strong: 'Your foundation is strong across the board. Keep doing what works, and use your lowest area as the next small step.',
      building: 'You have a solid base with clear room to improve. Your three biggest gaps are where small changes pay off first.',
      gaps: 'Several areas are holding you back. Start with your biggest gap: small, steady changes beat a big overhaul.'
    };
    var name = (contact.name || '').trim();
    var greeting = name ? 'Hi ' + esc(name) + ', here is your score' : 'Your score';

    var html = '';
    html += '<div class="tq__score-card">';
    html += '<span class="tq__eyebrow">' + greeting + '</span>';
    html += '<div class="tq__gauge" role="img" aria-label="Foundation Score ' + r.score + ' out of 100"><svg viewBox="0 0 200 200" aria-hidden="true"><circle class="tq__gauge-track" cx="100" cy="100" r="88"></circle><circle class="tq__gauge-value" data-gauge cx="100" cy="100" r="88" style="stroke:var(--tq-' + r.tier + ')"></circle></svg><div class="tq__gauge-num"><span data-gauge-num>0</span><small>out of 100</small></div></div>';
    html += '<span class="tq__tier tq__tier--' + r.tier + '">' + labels[r.tier] + '</span>';
    html += '<p class="tq__lead">' + esc(text[r.tier]) + '</p>';
    html += areasBlock();
    html += '</div>';

    html += offerBlock();
    html += gapsBlock();
    html += claimsBlock();
    html += proofBlock();
    html += '<p class="tq__disclaimer">This quiz is a lifestyle check, not a medical diagnosis. If you often feel unwell, very tired or low, speak to your doctor. Food supplements are not a substitute for a varied diet and a healthy lifestyle.</p>';
    html += '<div class="tq__stickybar"><a class="tq__btn" data-cta href="' + esc(cfg.productUrl + (cfg.productUrl.indexOf('?') > -1 ? '&' : '?') + 'utm_source=quiz&utm_medium=onsite&utm_campaign=foundation-score') + '">Claim my 20% off</a></div>';
    return screen(html, 'tq__result');
  }

  function animateGauge() {
    var ring = stage.querySelector('[data-gauge]');
    var num = stage.querySelector('[data-gauge-num]');
    if (!ring || !num || !result) return;
    var target = result.score;
    stage.querySelectorAll('[data-fill]').forEach(function (bar) {
      requestAnimationFrame(function () {
        bar.style.width = bar.getAttribute('data-fill') + '%';
      });
    });
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(function () {
      ring.style.strokeDashoffset = String(553 * (1 - target / 100));
    });
    if (reduce) {
      num.textContent = String(target);
      return;
    }
    var startTime = null;
    function tick(ts) {
      if (startTime === null) startTime = ts;
      var t = Math.min((ts - startTime) / 1400, 1);
      var eased = 1 - Math.pow(1 - t, 3);
      num.textContent = String(Math.round(target * eased));
      if (t < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* -------------------------------------------------------------- render -- */
  function render(id, isBack) {
    var html;
    if (id === 'intro') html = renderIntro(isBack);
    else if (id === 'reassure') html = renderReassure(isBack);
    else if (id === 'meet') html = renderMeet(isBack);
    else if (id === 'calc') html = renderCalc();
    else if (id === 'email') html = renderEmail(isBack);
    else if (id === 'result') html = renderResult();
    else html = renderQuestion(id, isBack);

    stage.innerHTML = html;

    var showTop = id !== 'intro' && id !== 'calc' && id !== 'result';
    topBar.hidden = !showTop;
    if (showTop) setProgress(id);

    var el = stage.firstElementChild;
    if (el) {
      try {
        el.focus({ preventScroll: true });
      } catch (e) {}
    }
    window.scrollTo(0, 0);

    if (id === 'calc') runCalc();
    if (id === 'email') bindEmail();
    if (id === 'result') {
      try {
        sessionStorage.setItem('ch_quiz', JSON.stringify({ v: 2, score: result.score, tier: result.tier, gap: result.gaps[0] }));
      } catch (e) {}
      animateGauge();
      track('result_viewed', { score: result.score, tier: result.tier, gaps: result.gaps.join(',') });
    }
  }

  function runCalc() {
    var steps = stage.querySelectorAll('[data-step]');
    compute();
    steps.forEach(function (s, i) {
      setTimeout(function () {
        if (i > 0) steps[i - 1].classList.replace('is-active', 'is-done');
        s.classList.add('is-active');
      }, i * 1000);
    });
    setTimeout(function () {
      steps[steps.length - 1].classList.replace('is-active', 'is-done');
    }, 2900);
    setTimeout(function () {
      if (current === 'calc') go('email');
    }, 3100);
  }

  /* --------------------------------------------------------------- email -- */
  var holder = root.querySelector('[data-tq-form-holder]');
  var emailForm = document.getElementById('ChQuizForm');

  function bindEmail() {
    var slot = stage.querySelector('[data-email-slot]');
    if (!slot || !emailForm) return;
    slot.appendChild(emailForm);

    var emailInput = emailForm.elements['contact[email]'];
    var nameInput = emailForm.elements['contact[first_name]'];
    var consent = emailForm.querySelector('[data-tq-consent]');
    var submit = emailForm.querySelector('[data-tq-email-submit]');
    var skip = emailForm.querySelector('[data-tq-skip]');

    function validEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
    }
    function refresh() {
      submit.disabled = !(validEmail(emailInput.value.trim()) && consent.checked);
    }
    emailInput.oninput = refresh;
    consent.onchange = refresh;
    refresh();

    skip.onclick = function () {
      track('email_skipped', { score: result.score, tier: result.tier });
      go('result');
    };

    // Runs just before Shopify's own submit handling. It must not cancel the
    // submit: the real form post is what saves the contact in Shopify.
    emailForm.onsubmit = function () {
      contact.name = nameInput.value.trim();
      contact.email = emailInput.value.trim();
      var tagList = [
        'quiz',
        'quiz-v2',
        'quiz-score-' + result.score,
        'quiz-tier-' + result.tier
      ];
      result.gaps.forEach(function (key) { tagList.push('quiz-gap-' + key); });
      if (source.utm_source) tagList.push('quiz-src-' + source.utm_source);
      if (source.utm_medium) tagList.push('quiz-med-' + source.utm_medium);
      if (source.utm_campaign) tagList.push('quiz-camp-' + source.utm_campaign);
      if (source.utm_content) tagList.push('quiz-ad-' + source.utm_content);
      emailForm.elements['contact[tags]'].value = tagList.join(', ');
      emailForm.elements['contact[accepts_marketing]'].value = consent.checked ? 'true' : 'false';
      try {
        sessionStorage.setItem(
          'ch_quiz_state',
          JSON.stringify({ t: Date.now(), answers: answers, points: points, contact: contact, result: result })
        );
      } catch (e) {}
      submit.textContent = 'One moment...';
      return true;
    };
  }

  // When Shopify has saved the contact it sends the visitor back to this page
  // with ?customer_posted=true. Put the quiz back where they were: the result.
  function restoreAfterPost() {
    // Shopify returns here with ?customer_posted=true, or via its /challenge
    // page (shown when it wants a human check) with no flag at all.
    var returned = /[?&]customer_posted=true/.test(location.search) || /\/(challenge|contact)/.test(document.referrer || '');
    if (!returned) return false;
    try {
      var saved = JSON.parse(sessionStorage.getItem('ch_quiz_state') || 'null');
      if (!saved || !saved.result || !saved.result.areas || Date.now() - (saved.t || 0) > 30 * 60 * 1000) return false;
      sessionStorage.removeItem('ch_quiz_state');
      answers = saved.answers || answers;
      points = saved.points || points;
      contact = saved.contact || contact;
      result = saved.result;
      started = true;
      current = 'result';
      history = [];
      track('email_submitted', { score: result.score, tier: result.tier, gaps: result.gaps.join(',') });
      render('result');
      return true;
    } catch (e) {
      return false;
    }
  }

  /* ------------------------------------------------------------- events -- */
  stage.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    if (target.closest('[data-start]')) {
      if (!started) {
        started = true;
        track('start', {});
      }
      go('q1');
      return;
    }

    var option = target.closest('.tq__option');
    if (option) {
      var id = option.getAttribute('data-q');
      var q = QUESTIONS[id];
      if (!q) return;
      stage.querySelectorAll('.tq__option').forEach(function (o) {
        o.classList.toggle('is-selected', o === option);
      });
      points[id] = Number(option.getAttribute('data-value'));
      answers[id] = option.getAttribute('data-label');
      track('answered', { question: id, number: q.n, area: q.area, answer: answers[id] });
      setTimeout(function () {
        if (current === id) go(nextOf(id));
      }, 220);
      return;
    }

    if (target.closest('[data-next]')) {
      go(nextOf(current));
      return;
    }

    if (target.closest('[data-cta]')) {
      track('cta_clicked', { score: result && result.score, tier: result && result.tier, gap: result && result.gaps[0] });
    }
  });

  // Read the live price of the quiz product so the result card is always right.
  (function loadPrice() {
    var path = (cfg.productUrl || '').split('?')[0];
    if (!path) return;
    fetch(path + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.variants || !p.variants.length) return;
        var fmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP' });
        var v = p.variants.reduce(function (a, b) { return b.price < a.price ? b : a; });
        cfg.livePrice = fmt.format(v.price / 100);
        if (v.compare_at_price && v.compare_at_price > v.price) {
          cfg.liveWas = fmt.format(v.compare_at_price / 100);
          cfg.livePct = Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price);
        }
      })
      .catch(function () {});
  })();

  if (!restoreAfterPost()) render('intro');
})();
